import { PortfolioCodeAssignmentResource } from 'app/_resources/portfolio-code.resource';
import { BalancingGroupAssignmentResource } from 'app/_resources/balancing-group.resource';
import { UtilService } from 'app/_services/util.service';
import * as angular from 'angular';
import * as moment from 'pkp-moment-timezone-all-years';
import { DateIntervalAsMoment } from '../custom';

export type BalancingOrPortfolioWizard = 'BALANCING' | 'PORTFOLIO';

export type ShipperAssignmentPreSelection = {
    shipperIdentifierList: Array<Shipper>;
    shippersWithRetiredAssignment: Array<Shipper>;
    unselectedShippers: Array<TsoShipperAssignmentsDto>;
    selectedShippers: Array<TsoShipperAssignmentsDto>;
};

export class BalancingAndPortfolioUploadWizardService {
    constructor(
        private $q: angular.IQService,
        private portfolioCodeAssignmentResource: PortfolioCodeAssignmentResource,
        private balancingGroupAssignmentResource: BalancingGroupAssignmentResource,
        private utilService: UtilService
    ) {}

    public preSelectShipperAssignments(
        shippersToPreSelect: Array<Shipper>,
        shippersWithAssignmentToTso: Array<TsoShipperAssignmentsDto>
    ): ShipperAssignmentPreSelection {
        const preSelection: ShipperAssignmentPreSelection = {
            selectedShippers: [],
            shippersWithRetiredAssignment: [],
            unselectedShippers: [],
            shipperIdentifierList: []
        };

        shippersWithAssignmentToTso.forEach(
            (shipperWithAssignmentToTso: TsoShipperAssignmentsDto) => {
                const selectedShipper: Shipper = shippersToPreSelect.find(
                    (shipper: Shipper) =>
                        shipper.id === shipperWithAssignmentToTso.shipperRefId
                );

                !angular.isUndefined(selectedShipper)
                    ? preSelection.selectedShippers.push(
                          shipperWithAssignmentToTso
                      )
                    : preSelection.unselectedShippers.push(
                          shipperWithAssignmentToTso
                      );
            }
        );

        if (
            shippersToPreSelect.length !== preSelection.selectedShippers.length
        ) {
            shippersToPreSelect.forEach((shipper: Shipper) => {
                const tsoShipperAssignmentDto =
                    preSelection.selectedShippers.find(
                        (shipperAssignment: TsoShipperAssignmentsDto) =>
                            shipperAssignment.shipperRefId === shipper.id
                    );

                !angular.isUndefined(tsoShipperAssignmentDto)
                    ? preSelection.shipperIdentifierList.push(shipper)
                    : preSelection.shippersWithRetiredAssignment.push(shipper);
            });
        }

        return preSelection;
    }

    public prepareNextWizardStage(
        shippersWithAssignmentToTso: Array<TsoShipperAssignmentsDto>,
        originalBalancingPortfolioCodeName: string,
        newBalancingPortfolioName: string,
        balancingOrPortfolioWizard: BalancingOrPortfolioWizard
    ): angular.IPromise<
        void | PortfolioCodeAssignmentDto | BalancingGroupAssignmentDto
    > {
        const deferred: angular.IDeferred<any> = this.$q.defer();

        if (!shippersWithAssignmentToTso) {
            deferred.reject();
            return deferred.promise;
        }

        if (originalBalancingPortfolioCodeName === newBalancingPortfolioName) {
            deferred.resolve();
            return deferred.promise;
        }

        let promise: angular.IPromise<
            PortfolioCodeAssignmentDto | BalancingGroupAssignmentDto
        >;

        if (balancingOrPortfolioWizard === 'BALANCING') {
            promise = this.fetchByName(
                newBalancingPortfolioName,
                balancingOrPortfolioWizard
            );
        } else if (balancingOrPortfolioWizard === 'PORTFOLIO') {
            promise = this.fetchByName(
                newBalancingPortfolioName,
                balancingOrPortfolioWizard
            );
        } else {
            throw Error(`There's no "${balancingOrPortfolioWizard}" wizard.`);
        }

        return promise;
    }

    public fetchByName(
        newBalancingPortfolioName: string,
        balancingOrPortfolioWizard: BalancingOrPortfolioWizard
    ): angular.IPromise<
        PortfolioCodeAssignmentDto | BalancingGroupAssignmentDto
    > {
        if (balancingOrPortfolioWizard === 'BALANCING') {
            return this.balancingGroupAssignmentResource.get({
                name: newBalancingPortfolioName
            }).$promise;
        } else if (balancingOrPortfolioWizard === 'PORTFOLIO') {
            return this.portfolioCodeAssignmentResource.get({
                name: newBalancingPortfolioName
            }).$promise;
        }

        throw Error(
            `"${balancingOrPortfolioWizard}" is not a supported wizard type.`
        );
    }

    public mapSelectedShippersToModel(
        selectedShippers: TsoShipperAssignmentsDto[]
    ): Shipper[] {
        return selectedShippers.map(
            (selectedShipper: TsoShipperAssignmentsDto) =>
                <Shipper>{
                    name: selectedShipper.shipperName,
                    id: selectedShipper.shipperRefId,
                    identifierList: this.createCompanyIdentifiers(
                        selectedShipper.eic,
                        selectedShipper.dvgw
                    )
                }
        );
    }

    public mapAccessPointsToModel(
        networkPoints: NetworkPointDto[]
    ): NetworkPointSimpleDto[] {
        if (networkPoints) {
            return networkPoints.map(
                (networkPoint: NetworkPointDto) =>
                    <NetworkPointSimpleDto>{
                        id: networkPoint.id,
                        id0: networkPoint.id0,
                        id1: networkPoint.id1,
                        identifier: networkPoint.identifier,
                        identifier0: networkPoint.identifier0,
                        identifier1: networkPoint.identifier1,
                        internalIdentifier0: networkPoint.internalIdentifier0,
                        internalIdentifier1: networkPoint.internalIdentifier1,
                        name: networkPoint.name,
                        name0: networkPoint.name0,
                        name1: networkPoint.name1,
                        bundled: networkPoint.bundled,
                        direction: networkPoint.direction,
                        tso0ShortName: networkPoint.tso0ShortName,
                        tso1ShortName: networkPoint.tso1ShortName,
                        tso0Id: networkPoint.tso0Id,
                        tso1Id: networkPoint.tso1Id,
                        marketArea0: networkPoint.marketArea0,
                        marketArea1: networkPoint.marketArea1,
                        authorityCode0: networkPoint.authorityCode0,
                        authorityCode1: networkPoint.authorityCode1,
                        version: networkPoint.version
                    }
            );
        }
        return [];
    }

    private createCompanyIdentifiers(
        eic: string,
        dvgw: string
    ): Array<CompanyIdDto> {
        const companyIdentifiers: Array<CompanyIdDto> = [];

        !!eic && companyIdentifiers.push({ type: 'EIC', id: eic });
        !!dvgw && companyIdentifiers.push({ type: 'DVGW', id: dvgw });

        return companyIdentifiers;
    }

    public preSetValidity(
        currentValidity: DateInterval = { start: null, end: null },
        balancingPortfolioExists: boolean
    ): DateInterval {
        const currentValidityAsMoment: DateIntervalAsMoment =
            this.utilService.toMoment(currentValidity);
        const newValidity: DateInterval = angular.copy(currentValidity);
        const now: moment.Moment = moment();
        const nextGasDayStart = moment(
            this.utilService.getNextGasDayStart(now.valueOf())
        );

        if (
            balancingPortfolioExists &&
            currentValidityAsMoment.start.isBefore(now)
        ) {
            newValidity.start = nextGasDayStart.toISOString();
        } else {
            newValidity.start = nextGasDayStart.toISOString();
            newValidity.end = new Date('9999-12-31T23:59+01:00').toISOString();
        }

        return newValidity;
    }
}

angular
    .module('app')
    .service(
        'balancingAndPortfolioUploadWizardService',
        BalancingAndPortfolioUploadWizardService
    );
